/**
 * DOM utility helpers.
 */
export default class Dom {
	/**
	 * Waits until the specified elements are fully rendered, visible, and stable.
	 * This ensures elements have a stable size before executing actions.
	 *
	 * @param {string | string[]} selectors - A single CSS selector or an array of selectors.
	 * @param {() => void} callback - The function to execute once all observed elements are stable.
	 * @returns {void}
	 */
	static waitForElementsReady(selectors, callback) {
		if (!Array.isArray(selectors)) {
			selectors = [selectors];
		}

		let observer;
		let frameCheckCounter = 0;
		const requiredStableFrames = 10;
		let alreadyTriggered = false;

		function elementsAreStable() {
			return selectors.every((selector) => {
				const elements = document.querySelectorAll(selector);
				if (elements.length === 0) return false;

				return Array.from(elements).every((el) => {
					const width = el.clientWidth;
					const height = el.clientHeight;
					return width > 0 && height > 0;
				});
			});
		}

		function checkStability() {
			if (alreadyTriggered) return;

			if (elementsAreStable()) {
				frameCheckCounter++;
				if (frameCheckCounter >= requiredStableFrames) {
					alreadyTriggered = true;
					observer.disconnect();
					callback();
				} else {
					requestAnimationFrame(checkStability);
				}
			} else {
				frameCheckCounter = 0;
				requestAnimationFrame(checkStability);
			}
		}

		observer = new MutationObserver(() => {
			if (!alreadyTriggered) {
				frameCheckCounter = 0;
				requestAnimationFrame(checkStability);
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });

		requestAnimationFrame(checkStability);
	}
}
